/**
 * PipelineGraphView — read-only visualization of a pipeline's workflow graph.
 *
 * Presentational only. The parent passes the backend serialized graph
 * (`{ nodes, edges, nodeConfigs, stateSchema }`); this component converts it
 * to React Flow shape (with elkjs auto-layout) via the shared
 * `convertBackendGraphToReactFlow` util and renders it inside a fixed-height
 * container using the existing `GraphFlowEditor` in `readonly` mode.
 *
 * Node detail on click:
 *   GraphFlowEditor's own `onNodeClick` early-returns when `readonly` is true
 *   (see organisms/GraphFlowEditor.js) — so it never opens its config modal in
 *   view-only mode. To still let users inspect "what's inside a node" we attach
 *   a delegated click listener on the wrapping container: React Flow renders
 *   each node as a `.react-flow__node` element carrying a `data-id` attribute,
 *   so we resolve the clicked node id from the DOM and open a lightweight
 *   read-only detail drawer that surfaces `nodeConfigs[nodeId]` (prompt /
 *   skills-tools / type / execute code). No inputs — purely a readout.
 *
 * Defensive: a falsy graph or one with no nodes renders a clean empty state
 * instead of crashing.
 */
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  GraphFlowEditor,
  convertBackendGraphToReactFlow,
} from '../../graph-workflow-editor';
import './PipelineGraphView.css';

const EMPTY_FLOW = { nodes: [], edges: [], nodeConfigs: {}, stateSchema: null };

const toTitleCase = (str) =>
  String(str || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const PipelineGraphView = ({ graph }) => {
  const [flow, setFlow] = useState(EMPTY_FLOW);
  const [loading, setLoading] = useState(false);
  // The node id whose detail drawer is open (null = closed).
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const containerRef = useRef(null);

  const hasGraph = !!(graph && Array.isArray(graph.nodes) && graph.nodes.length > 0);

  // Convert backend graph → React Flow shape (async: elkjs auto-layout).
  // Guarded against out-of-order resolutions if `graph` changes mid-flight.
  useEffect(() => {
    let cancelled = false;
    setSelectedNodeId(null);
    if (!hasGraph) {
      setFlow(EMPTY_FLOW);
      return undefined;
    }
    setLoading(true);
    convertBackendGraphToReactFlow(graph)
      .then((result) => {
        if (cancelled) return;
        setFlow(result || EMPTY_FLOW);
      })
      .catch(() => {
        if (!cancelled) setFlow(EMPTY_FLOW);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [graph, hasGraph]);

  // Delegated node-click handler. GraphFlowEditor suppresses node clicks in
  // readonly mode, so we listen on the wrapper and resolve the React Flow
  // node element (`.react-flow__node[data-id]`) the user clicked.
  const handleContainerClick = useCallback((event) => {
    const nodeEl = event.target.closest?.('.react-flow__node');
    if (!nodeEl) return;
    const nodeId = nodeEl.getAttribute('data-id');
    if (nodeId) setSelectedNodeId(nodeId);
  }, []);

  const selectedNode = useMemo(
    () => flow.nodes.find((n) => n.id === selectedNodeId) || null,
    [flow.nodes, selectedNodeId],
  );
  const selectedConfig = useMemo(
    () => (selectedNodeId ? flow.nodeConfigs?.[selectedNodeId] || {} : {}),
    [flow.nodeConfigs, selectedNodeId],
  );

  if (!hasGraph) {
    return (
      <div className="pipeline-graph-view pipeline-graph-view--empty">
        <div className="pipeline-graph-view__empty-text">
          Graph not available for this pipeline.
        </div>
      </div>
    );
  }

  const label = selectedNode?.data?.label || selectedNodeId || 'Node';
  const nodeType = selectedNode?.data?.nodeType || selectedNode?.type || null;
  const prompt = selectedConfig.prompt || null;
  const extra = selectedConfig.extraPromptInstructions || null;
  const tools = Array.isArray(selectedConfig.tools) ? selectedConfig.tools : [];
  const executeCode = selectedConfig.executeCode || selectedConfig.customCode || null;

  return (
    <div className="pipeline-graph-view">
      <div
        className="pipeline-graph-view__canvas"
        ref={containerRef}
        onClick={handleContainerClick}
      >
        {loading && (
          <div className="pipeline-graph-view__loading">Laying out graph…</div>
        )}
        <GraphFlowEditor
          readonly
          syncFromProps
          initialNodes={flow.nodes}
          initialEdges={flow.edges}
          nodeConfigs={flow.nodeConfigs}
          stateSchema={flow.stateSchema}
          showResetPanel={false}
          showGraphInfoPanel={false}
        />
      </div>

      {selectedNodeId && (
        <>
          <div
            className="pipeline-graph-view__drawer-backdrop"
            onClick={() => setSelectedNodeId(null)}
            role="presentation"
          />
          <aside
            className="pipeline-graph-view__drawer"
            role="dialog"
            aria-label={`${label} details`}
          >
            <div className="pipeline-graph-view__drawer-head">
              <div className="pipeline-graph-view__drawer-titlewrap">
                <h3 className="pipeline-graph-view__drawer-title">{label}</h3>
                {nodeType && (
                  <span className="pipeline-graph-view__drawer-type">
                    {toTitleCase(nodeType)}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="pipeline-graph-view__drawer-close"
                onClick={() => setSelectedNodeId(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="pipeline-graph-view__drawer-body">
              <section className="pipeline-graph-view__field">
                <div className="pipeline-graph-view__field-label">Node ID</div>
                <code className="pipeline-graph-view__field-mono">{selectedNodeId}</code>
              </section>

              {prompt ? (
                <section className="pipeline-graph-view__field">
                  <div className="pipeline-graph-view__field-label">Prompt</div>
                  <pre className="pipeline-graph-view__field-pre">{prompt}</pre>
                </section>
              ) : null}

              {extra ? (
                <section className="pipeline-graph-view__field">
                  <div className="pipeline-graph-view__field-label">
                    Extra instructions
                  </div>
                  <pre className="pipeline-graph-view__field-pre">{extra}</pre>
                </section>
              ) : null}

              <section className="pipeline-graph-view__field">
                <div className="pipeline-graph-view__field-label">
                  Skills / Tools
                </div>
                {tools.length > 0 ? (
                  <div className="pipeline-graph-view__chips">
                    {tools.map((t) => (
                      <span key={t} className="pipeline-graph-view__chip">
                        {t}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="pipeline-graph-view__field-muted">None</div>
                )}
              </section>

              {executeCode ? (
                <section className="pipeline-graph-view__field">
                  <div className="pipeline-graph-view__field-label">execute.js</div>
                  <pre className="pipeline-graph-view__field-pre pipeline-graph-view__field-pre--code">
                    {executeCode}
                  </pre>
                </section>
              ) : null}

              {!prompt && !extra && tools.length === 0 && !executeCode && (
                <div className="pipeline-graph-view__field-muted">
                  This node has no additional configuration.
                </div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default PipelineGraphView;
